import React, { useEffect, useMemo, useRef, useState } from "react";
import { QrScanner } from "@yudiel/react-qr-scanner";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";
import { formatAmountWithCommas } from "../../content_panel/utils/helpers";
import DeviceIdsViewer from "../common-components/DeviceIdsViewer";
import Icon from "@mdi/react";
import {
  mdiBarcodeScan,
  mdiAlertCircle,
  mdiCloseCircle,
  mdiAlertOutline,
  mdiPackageVariantClosed,
} from "@mdi/js";
import Papa from "papaparse";
import { ItemWarehouseModel } from "../model/warehouse_inward_models";
import {
  ScanAndGetDevicesOnlyAPI,
  ScannedBarCodeScanForDeviceGRNAPI,
} from "../../../../erp/accounts/vouchers/purchase/service/inward_repo";
import DeviceScanModalInward from "./DeviceScanInwradModal";
import CurrencyBadge from "./CurrencyBadge";
import { useWarehouseInward } from "../contexts/InwardContext";
import {
  calculateIsBlockedByDispute,
  isBulkSTInwardAllowed,
} from "../warehouse_helper";

interface InwardReceiveItemCardSTProps {
  item: ItemWarehouseModel;
  allocatedDevices: string[]; // For ST: devices that SHOULD be in this shipment
  inwardedDevices: string[]; // Devices already inwarded in previous GRNs
  error?: string;
  onDeviceSelection: (selection: DeviceSelection) => void;
  onRateChange?: (rate: number) => void;
  onPhysicalQtyChange?: (qty: number) => void;
  onInvoiceQtyChange?: (qty: number) => void;
  onTotalAmountChange?: (amount: number) => void;
  invoiceQty?: number;
  invoiceRate?: number;
  physicalQty?: number;
  invoiceTotalAmount?: number;
  currency?: string;
  vendor_currency_to_inr?: number;
}

export interface DeviceSelection {
  matched: string[];
  unmatched: string[];
  quantity: number;
  viaBulk?: boolean;
}

interface ValidationResult {
  valid: boolean;
  reason?: "not_allocated" | "already_inwarded" | "duplicate_scan";
  deviceId?: string;
}

interface RejectedDevice {
  id: string;
  reason: string;
}

const InwardReceiveItemCardST: React.FC<InwardReceiveItemCardSTProps> = ({
  item,
  allocatedDevices,
  inwardedDevices,
  error,
  onDeviceSelection,
  onRateChange,
  onPhysicalQtyChange,
  onInvoiceQtyChange,
  onTotalAmountChange,
  invoiceQty = 0,
  invoiceRate = 0,
  physicalQty = 0,
  invoiceTotalAmount = 0,
  currency,
  vendor_currency_to_inr,
}) => {
  const ordered = item.requestedQty ?? 0;
  const received = item.receivedQty ?? 0;
  const pending = Math.max(ordered - received, 0);

  const { showToast } = useToast();

  // Local UI state
  const [scanning, setScanning] = useState(false);
  const [matchedDevices, setMatchedDevices] = useState<string[]>([]);
  const [rejectedDevices, setRejectedDevices] = useState<RejectedDevice[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const processingRef = useRef<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<"scan" | "bulk" | "upload">(
    "scan",
  );
  const [bulkInput, setBulkInput] = useState("");
  const [usedBulkEntry, setUsedBulkEntry] = useState(false);
  const [barcodeInputValue, setBarcodeInputValue] = useState("");
  const barcodeInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      const clean = barcodeInputValue.trim();
      if (clean && !isProcessing) {
        if (
          clean.length === 8 ||
          (currentInwardOrder?.type === "PO" && clean.length === 6)
        ) {
          handleScanResult(clean);
          setBarcodeInputValue("");
        }
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [barcodeInputValue, isProcessing]);

  const { currentInwardOrder } = useWarehouseInward();
  const isFinishedGoods = item.subCategory === "Finished Goods";
  const bulkInwardAllowed = isBulkSTInwardAllowed(currentInwardOrder);
  const isBlockedByDispute = useMemo(
    () =>
      calculateIsBlockedByDispute(
        currentInwardOrder?.past_disputes,
        currentInwardOrder?.is_disputed,
      ),
    [currentInwardOrder?.past_disputes, currentInwardOrder?.is_disputed],
  );

  const displayPhysicalQty = isFinishedGoods
    ? matchedDevices.length
    : physicalQty;

  const remainingForST =
    currentInwardOrder?.type === "ST"
      ? allocatedDevices.length - inwardedDevices.length
      : pending;

  useEffect(() => {
    // For FG, we strictly follow scanned quantity for physical receipt
    onDeviceSelection({
      matched: matchedDevices,
      unmatched: rejectedDevices.map((d) => d.id),
      quantity: displayPhysicalQty,
      viaBulk: usedBulkEntry,
    });
  }, [
    matchedDevices,
    rejectedDevices,
    displayPhysicalQty,
    isFinishedGoods,
    usedBulkEntry,
  ]);

  // Validate device based on inward type
  const validateDevice = (
    deviceId: string,
    mode: "single" | "box" = "box",
  ): ValidationResult => {
    // Check if already scanned in this session
    if (matchedDevices.includes(deviceId)) {
      return { valid: false, reason: "duplicate_scan", deviceId };
    }

    // ST: only devices allocated to this ST can be inwarded, and never twice —
    // enforced in every scan mode (single / box / bulk / CSV), matching the backend.
    if (currentInwardOrder?.type === "ST") {
      if (!allocatedDevices.includes(deviceId)) {
        return { valid: false, reason: "not_allocated", deviceId };
      }
      if (inwardedDevices.includes(deviceId)) {
        return { valid: false, reason: "already_inwarded", deviceId };
      }
      return { valid: true };
    }

    // MODE: SINGLE (6-digit scan)
    // Only check allocation
    if (mode === "single" && currentInwardOrder?.type !== "PO") {
      // commenting out allocation check for now since we are allowing free scanning for PO as well

      if (!allocatedDevices.includes(deviceId)) {
        return { valid: false, reason: "not_allocated", deviceId };
      }

      return { valid: true };
    }

    // MODE: BOX (8-digit scan)

    // Check if already inwarded in previous GRNs
    if (inwardedDevices.includes(deviceId)) {
      return { valid: false, reason: "already_inwarded", deviceId };
    }

    // PO-specific validation
    if (currentInwardOrder?.type === "PO") {
      const remaining = pending - matchedDevices.length;
      if (remaining <= 0) {
        return { valid: false, reason: "duplicate_scan", deviceId };
      }
    }

    return { valid: true };
  };

  // Get validation error message
  const getValidationMessage = (reason: string): string => {
    const messages: Record<string, string> = {
      not_allocated: `Not allocated to this ${currentInwardOrder?.type}`,
      already_inwarded: "Already inwarded in previous GRN",
      duplicate_scan: "Already scanned in this session",
    };
    return messages[reason] || "Invalid device";
  };

  const processBatchDevices = (
    deviceIds: string[],
    mode: "single" | "box" = "box",
  ): {
    valid: string[];
    rejected: RejectedDevice[];
    duplicatesRemoved: number;
  } => {
    // Remove duplicates
    const uniqueIds = Array.from(new Set(deviceIds));
    const duplicatesRemoved = deviceIds.length - uniqueIds.length;

    const valid: string[] = [];
    const rejected: RejectedDevice[] = [];

    uniqueIds.forEach((deviceId) => {
      const validation = validateDevice(deviceId, mode);

      if (validation.valid) {
        valid.push(deviceId);
      } else {
        rejected.push({
          id: deviceId,
          reason: getValidationMessage(validation.reason!),
        });
      }
    });

    return { valid, rejected, duplicatesRemoved };
  };

  // Handle PO scan - scans individual device QR
  const handlePOScanResult = async (raw: string) => {
    const clean = raw.trim().replace(/\s+/g, "");

    if (clean.length !== 6 && clean.length !== 8) {
      showToast({ type: ToastType.error, text: "Invalid device QR format" });
      return;
    }

    if (isProcessing) {
      showToast({
        type: ToastType.warning,
        text: "Processing previous scan...",
      });
      return;
    }

    if (processingRef.current.has(clean)) {
      showToast({
        type: ToastType.warning,
        text: "Already processed this QR",
      });
      return;
    }

    setIsProcessing(true);
    processingRef.current.add(clean);

    try {
      let fetchedIds: string[] = [];

      if (clean.length === 8 || clean.length === 6) {
        const res = await ScannedBarCodeScanForDeviceGRNAPI(
          item.inventoryItemCode,
          clean,
        );

        if (!res.success || !Array.isArray(res.devices)) {
          showToast({
            type: ToastType.error,
            text: res.error || "No devices found",
          });
          return;
        }

        fetchedIds = Array.from(
          new Set(
            res.devices
              .map((d: any) => d?.unique_id)
              .filter((id: any): id is string => typeof id === "string"),
          ),
        );
      }

      if (!fetchedIds.length) {
        showToast({
          type: ToastType.error,
          text: "No valid devices found",
        });
        return;
      }

      const { valid, rejected, duplicatesRemoved } = processBatchDevices(
        fetchedIds,
        clean.length === 6 ? "single" : "box",
      );

      const remaining = invoiceQty - matchedDevices.length;

      if (remaining <= 0) {
        showToast({
          type: ToastType.warning,
          text: "Invoice quantity already fulfilled... Inward will be based on invoice quantity",
        });
        return;
      }

      const toAdd = valid.slice(0, remaining);
      const overflow = valid.slice(remaining);

      if (toAdd.length > 0) {
        setMatchedDevices((prev) => [...prev, ...toAdd]);

        if (!isFinishedGoods) {
          onPhysicalQtyChange?.(
            Math.min(invoiceQty, physicalQty + toAdd.length),
          );
        }
      }

      const allRejected = [
        ...rejected,
        ...overflow.map((id) => ({
          id,
          reason: "Exceeds pending quantity",
        })),
      ];

      if (allRejected.length > 0) {
        setRejectedDevices((prev) => [...prev, ...allRejected]);
      }

      let message = `✅ Valid: ${toAdd.length}`;
      if (rejected.length > 0) message += ` | ❌ Invalid: ${rejected.length}`;
      if (overflow.length > 0) message += ` | ⚠️ Overflow: ${overflow.length}`;
      if (duplicatesRemoved > 0)
        message += ` | 🔄 Duplicates removed: ${duplicatesRemoved}`;

      showToast({
        type: toAdd.length > 0 ? ToastType.success : ToastType.warning,
        text: message,
      });
    } catch (e: any) {
      showToast({
        type: ToastType.error,
        text: e.message || "Device scan failed",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle ST scan - scans box QR (8 digits)
  const handleSTScanResult = async (raw: string) => {
    const clean = raw.trim().replace(/\s+/g, "");

    if (clean.length !== 6 && clean.length !== 8) {
      showToast({
        type: ToastType.error,
        text: "Invalid QR code (must be 6 or 8 digits)",
      });
      return;
    }

    if (isProcessing) {
      showToast({
        type: ToastType.warning,
        text: "Processing previous scan...",
      });
      return;
    }

    if (processingRef.current.has(clean)) {
      showToast({
        type: ToastType.warning,
        text: "Already processed this box QR",
      });
      return;
    }

    setIsProcessing(true);
    processingRef.current.add(clean);

    try {
      let devices: string[] = [];

      if (clean.length === 6) {
        // 6-digit: treat the scanned value as the device ID itself.
        // This allows scanning individual devices even in ST for flexibility.
        devices = [clean];
      } else {
        const res = await ScanAndGetDevicesOnlyAPI(clean);

        if (!res.success || !Array.isArray(res.devices)) {
          showToast({
            type: ToastType.error,
            text: "No devices found for this QR",
          });
          return;
        }

        devices = res.devices;
      }

      if (!devices.length) {
        showToast({
          type: ToastType.warning,
          text: "No devices found for this item in the scanned box",
        });
        return;
      }

      const { valid, rejected, duplicatesRemoved } = processBatchDevices(
        devices,
        clean.length === 6 ? "single" : "box",
      );

      const remaining = remainingForST - matchedDevices.length;

      if (remaining <= 0) {
        showToast({
          type: ToastType.warning,
          text: "All allocated devices already scanned",
        });
        return;
      }

      const toAdd = valid.slice(0, remaining);
      const overflow = valid.slice(remaining);

      if (toAdd.length > 0) {
        setMatchedDevices((prev) => [...prev, ...toAdd]);
        if (!isFinishedGoods) {
          onPhysicalQtyChange?.(
            Math.min(remainingForST, physicalQty + toAdd.length),
          );
        }
      }

      // ================================
      // TRACK REJECTED
      // ================================
      const allRejected = [
        ...rejected,
        ...overflow.map((id) => ({
          id,
          reason: "Exceeds allocated quantity",
        })),
      ];

      if (allRejected.length > 0) {
        setRejectedDevices((prev) => [...prev, ...allRejected]);
      }

      // ================================
      // USER FEEDBACK
      // ================================
      let message = `📦 Scanned: ${devices.length} | ✅ Valid: ${toAdd.length}`;
      if (rejected.length > 0) message += ` | ❌ Invalid: ${rejected.length}`;
      if (overflow.length > 0) message += ` | ⚠️ Overflow: ${overflow.length}`;
      if (duplicatesRemoved > 0)
        message += ` | 🔄 Duplicates: ${duplicatesRemoved}`;

      showToast({
        type: toAdd.length > 0 ? ToastType.success : ToastType.warning,
        text: message,
      });
    } catch (e: any) {
      showToast({
        type: ToastType.error,
        text: e.message || "Box scan failed",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Route scan based on type
  const handleScanResult = (raw: string) => {
    if (isBlockedByDispute) {
      showToast({
        type: ToastType.error,
        text: "Scanning blocked due to unresolved disputes",
      });
      return;
    }
    if (currentInwardOrder?.type === "PO") {
      handlePOScanResult(raw);
    } else {
      handleSTScanResult(raw);
    }
  };

  // Paste and CSV accept individual 6-character device IDs only (no box QRs)
  const addBulkDeviceIds = (ids: string[], sourceLabel: string) => {
    if (!bulkInwardAllowed) return;

    const formatRejected: RejectedDevice[] = [];
    const wellFormed: string[] = [];
    ids.forEach((raw) => {
      // Old labels can read "snv79n_1": keep it if that exact id is on this ST,
      // otherwise use the plain 6-character id (same rule as the scan APIs).
      const suffixed = raw.match(/^([a-z0-9]{6})_\d+$/i);
      const id =
        suffixed && !allocatedDevices.includes(raw) ? suffixed[1] : raw;
      if (/^[a-z0-9]{6}$/i.test(id) || allocatedDevices.includes(id))
        wellFormed.push(id);
      else
        formatRejected.push({
          id,
          reason: "Invalid device ID (must be 6 letters/digits)",
        });
    });

    const remaining = remainingForST - matchedDevices.length;
    if (remaining <= 0) {
      showToast({
        type: ToastType.warning,
        text: "All allocated devices already scanned",
      });
      return;
    }

    const { valid, rejected, duplicatesRemoved } = processBatchDevices(
      wellFormed,
      "single",
    );

    const toAdd = valid.slice(0, remaining);
    const overflow = valid.slice(remaining);

    if (toAdd.length > 0) {
      setMatchedDevices((prev) => [...prev, ...toAdd]);
      setUsedBulkEntry(true);
      if (!isFinishedGoods) {
        onPhysicalQtyChange?.(
          Math.min(remainingForST, physicalQty + toAdd.length),
        );
      }
    }

    const allRejected = [
      ...formatRejected,
      ...rejected,
      ...overflow.map((id) => ({
        id,
        reason: "Exceeds allocated quantity",
      })),
    ];

    if (allRejected.length > 0) {
      setRejectedDevices((prev) => [...prev, ...allRejected]);
    }

    const invalidCount = formatRejected.length + rejected.length;
    let message = `${sourceLabel}: ${ids.length} | ✅ Added: ${toAdd.length}`;
    if (invalidCount > 0) message += ` | ❌ Rejected: ${invalidCount}`;
    if (overflow.length > 0) message += ` | ⚠️ Overflow: ${overflow.length}`;
    if (duplicatesRemoved > 0)
      message += ` | 🔄 Duplicates removed: ${duplicatesRemoved}`;

    showToast({
      type: toAdd.length > 0 ? ToastType.success : ToastType.warning,
      text: message,
    });
  };

  const handleBulkPaste = () => {
    const ids = bulkInput
      .split(/[\s,;]+/)
      .map((v) => v.trim())
      .filter(Boolean);

    if (!ids.length) {
      showToast({
        type: ToastType.warning,
        text: "Please paste device IDs",
      });
      return;
    }

    addBulkDeviceIds(ids, "📋 Pasted");
    setBulkInput("");
  };

  const handleCSVUpload = (file: File) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (h) => h.trim(),
      complete: (result) => {
        const rows = result.data as Record<string, any>[];

        if (!result.meta.fields?.includes("Unique ID")) {
          showToast({
            type: ToastType.error,
            text: 'CSV must contain "Unique ID" column',
          });
          return;
        }

        const parsedIds = rows
          .map((r) => String(r["Unique ID"] || "").trim())
          .filter(Boolean);

        if (!parsedIds.length) {
          showToast({
            type: ToastType.error,
            text: "No valid device IDs found in CSV",
          });
          return;
        }

        addBulkDeviceIds(parsedIds, "📄 CSV rows");
      },
      error: () => {
        showToast({
          type: ToastType.error,
          text: "Failed to parse CSV file",
        });
      },
    });
  };

  const handleDownloadSampleCSV = () => {
    const blob = new Blob(["Unique ID\n123456\n234567\n"], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "st_inward_device_ids_sample.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  // Clear all scanned devices
  const handleClearAll = () => {
    if (
      !window.confirm(
        "Are you sure you want to clear all scanned devices? This action cannot be undone.",
      )
    ) {
      return;
    }

    setMatchedDevices([]);
    setRejectedDevices([]);
    setUsedBulkEntry(false);
    processingRef.current.clear();
    showToast({
      type: ToastType.success,
      text: "All devices cleared",
    });
  };

  const handleClearRejected = () => {
    setRejectedDevices([]);
  };

  // Calculate missing devices for ST
  const missingDevices = React.useMemo(() => {
    if (currentInwardOrder?.type !== "ST") return [];

    return allocatedDevices.filter(
      (id) => !matchedDevices.includes(id) && !inwardedDevices.includes(id),
    );
  }, [
    currentInwardOrder?.type,
    allocatedDevices,
    matchedDevices,
    inwardedDevices,
  ]);

  useEffect(() => {
    setMatchedDevices([]);
    setRejectedDevices([]);
    setBulkInput("");
    processingRef.current.clear();
  }, [item.inventoryItemCode]);

  const renderRejectedDevices = () => {
    if (rejectedDevices.length === 0) return null;

    const grouped = rejectedDevices.reduce(
      (acc, d) => {
        if (!acc[d.reason]) acc[d.reason] = [];
        acc[d.reason].push(d.id);
        return acc;
      },
      {} as Record<string, string[]>,
    );

    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-semibold text-red-700">
            <Icon path={mdiCloseCircle} size={0.7} />
            Rejected Devices ({rejectedDevices.length})
          </div>
          <button
            type="button"
            onClick={handleClearRejected}
            className="text-xs text-red-600 hover:underline"
          >
            Clear List
          </button>
        </div>

        <div className="space-y-2">
          {Object.entries(grouped).map(([reason, ids]) => (
            <div key={reason} className="text-xs">
              <p className="font-semibold text-red-800 mb-1">
                {reason} ({ids.length})
              </p>
              <div className="max-h-24 overflow-y-auto">
                <div className="flex flex-wrap gap-1">
                  {ids.slice(0, 10).map((id, i) => (
                    <span
                      key={i}
                      className="px-2 py-0.5 border border-red-300 rounded bg-white font-mono text-xs text-red-700"
                    >
                      {id}
                    </span>
                  ))}
                  {ids.length > 10 && (
                    <span className="text-xs text-red-700 px-2 py-0.5">
                      +{ids.length - 10} more
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div
      id={`warehouse-inward-receive-item-card-${item.inventoryItemCode}`}
      className="border bg-gray-100 rounded-lg p-4 space-y-4"
    >
      {/* Header */}
      <div className="flex flex-row justify-between">
        <div className="min-w-0 pr-4">
          <div className="font-semibold">{item?.itemName}</div>
          <div className="text-sm text-gray-500">{item?.inventoryItemCode}</div>
          {item?.itemDescription && (
            <div
              className="text-xs text-gray-500 italic mt-1 line-clamp-2"
              title={item.itemDescription}
            >
              {item.itemDescription}
            </div>
          )}
        </div>
        <div>
          <div className="font-semibold">
            ₹ {formatAmountWithCommas((item?.itemPrice ?? 0) * ordered)}
          </div>
          <div className="text-xs text-gray-500">
            @ ₹ {formatAmountWithCommas(item?.itemPrice ?? 0)}/unit
          </div>
          <CurrencyBadge
            currency={currency}
            rate={vendor_currency_to_inr}
            inrAmount={item?.itemPrice ?? 0}
            perUnit
          />
        </div>
      </div>

      <div className="flex items-center gap-3 rounded-md bg-blue-100 px-3 py-1.5 text-sm flex-wrap">
        <Icon
          path={mdiPackageVariantClosed}
          size={0.8}
          className="text-blue-600"
        />

        <span>
          Already Received: <strong>{received}</strong> of {ordered}
        </span>

        <span className="text-muted-foreground">|</span>

        <span>
          Receivable: <strong>{pending}</strong>
        </span>
      </div>

      {/* Qty summary and Inputs for quantites */}
      <div className="grid md:grid-cols-3 grid-cols-2 gap-4 text-sm">
        <div className="flex flex-col gap-1 md:gap-2">
          <p className="text-gray-500">PO Quantity</p>
          <p className="w-full font-semibold border rounded-md px-3 py-2 input-warning text-center cursor-not-allowed">
            {ordered}
          </p>
        </div>

        {/* input */}
        <div className="flex flex-col gap-1 md:gap-2">
          <p className="text-gray-500">Invoice Quantity</p>
          <input
            id={`warehouse-inward-receive-item-${item.inventoryItemCode}-invoice-qty`}
            type="number"
            min={0}
            max={pending}
            step={1}
            value={invoiceQty}
            inputMode="numeric"
            pattern="[0-9]*"
            disabled={pending === 0 || isBlockedByDispute}
            title={
              isBlockedByDispute
                ? "Input blocked due to unresolved disputes"
                : ""
            }
            onChange={(e) => {
              const value = Number(e.target.value);
              onInvoiceQtyChange?.(value);
            }}
            className={`w-full border-2 rounded-md px-3 py-2 focus:outline-none transition-all ${
              pending === 0 || isBlockedByDispute
                ? "bg-yellow-50 border-yellow-200 text-yellow-700 cursor-not-allowed"
                : "bg-white border-purple-300 focus:border-primary focus:ring-1 focus:ring-primary font-semibold"
            }`}
          />
        </div>

        {/* input */}
        <div className="flex flex-col gap-1 md:gap-2">
          <p className="text-gray-500">Physical Quantity</p>
          <input
            id={`warehouse-inward-receive-item-${item.inventoryItemCode}-physical-qty`}
            type="number"
            min={0}
            max={pending}
            step={1}
            value={physicalQty || ""}
            inputMode="numeric"
            pattern="[0-9]*"
            disabled={pending === 0 || isFinishedGoods || isBlockedByDispute}
            title={
              isBlockedByDispute
                ? "Input blocked due to unresolved disputes"
                : ""
            }
            onChange={(e) => {
              const value = Number(e.target.value);
              onPhysicalQtyChange?.(value);
            }}
            className={`w-full border-2 rounded-md px-3 py-2 focus:outline-none transition-all ${
              pending === 0 || isFinishedGoods || isBlockedByDispute
                ? "input-warning cursor-not-allowed"
                : "bg-white border-purple-300 focus:border-primary focus:ring-1 focus:ring-primary font-semibold"
            }`}
          />
        </div>
      </div>

      {/* Quantity Mismatch Error message */}
      {((invoiceQty > 0 && invoiceQty !== ordered) ||
        (physicalQty > 0 && physicalQty !== ordered)) && (
        <div className="text-warning text-xs">
          <Icon path={mdiAlertOutline} size={0.6} className="inline mr-1" />{" "}
          Quantity mismatch detected
        </div>
      )}

      {/* Rate Matching and Totals */}
      {currentInwardOrder?.type === "PO" && (
        <div className="grid md:grid-cols-3 grid-cols-2 gap-4 text-sm">
          <div className="flex flex-col gap-1 md:gap-2">
            <p className="text-gray-500">PO Rate</p>
            <p className="w-full font-semibold border rounded-md px-3 py-2 input-warning text-center">
              {item.itemPrice}
            </p>
          </div>

          {/* input */}
          <div className="flex flex-col gap-1 md:gap-2">
            <p className="text-gray-500">Invoice Rate / unit</p>
            <input
              id={`warehouse-inward-receive-item-${item.inventoryItemCode}-invoice-rate`}
              type="number"
              min={0}
              step={0.01}
              value={invoiceRate || ""}
              inputMode="decimal"
              disabled={pending === 0 || isBlockedByDispute}
              title={
                isBlockedByDispute
                  ? "Input blocked due to unresolved disputes"
                  : ""
              }
              onChange={(e) => {
                const value = Number(e.target.value);
                onRateChange?.(value);
              }}
              className={`w-full border-2 rounded-md px-3 py-2 focus:outline-none transition-all ${
                pending === 0 || isBlockedByDispute
                  ? "bg-yellow-50 border-yellow-200 text-yellow-700 cursor-not-allowed"
                  : "bg-white border-purple-300 focus:border-primary focus:ring-1 focus:ring-primary font-semibold"
              }`}
            />
          </div>

          <div className="flex flex-col gap-1 md:gap-2">
            <p className="text-gray-500">Total Invoice Amount</p>
            <input
              id={`warehouse-inward-receive-item-${item.inventoryItemCode}-invoice-total`}
              type="number"
              min={0}
              step={0.01}
              value={invoiceTotalAmount || ""}
              inputMode="decimal"
              disabled={pending === 0 || isBlockedByDispute}
              title={
                isBlockedByDispute
                  ? "Input blocked due to unresolved disputes"
                  : ""
              }
              onChange={(e) => {
                const value = Number(e.target.value);
                onTotalAmountChange?.(value);
              }}
              className={`w-full border-2 rounded-md px-3 py-2 focus:outline-none transition-all ${
                pending === 0 || isBlockedByDispute
                  ? "bg-amber-50 border-amber-200 text-amber-700 cursor-not-allowed"
                  : "bg-white border-purple-300 focus:border-primary focus:ring-1 focus:ring-primary font-semibold"
              }`}
            />
          </div>
        </div>
      )}

      {/* Rate Mismatch Error message */}
      {currentInwardOrder?.type === "PO" &&
        invoiceRate > 0 &&
        invoiceRate !== item.itemPrice && (
          <div className="text-warning text-xs">
            <Icon path={mdiAlertOutline} size={0.6} className="inline mr-1" /> ₹
            Rate mismatch detected — PO: ₹{item.itemPrice} vs Invoice: ₹
            {invoiceRate}
          </div>
        )}

      {/* Dispute handled at page level */}
      {isFinishedGoods && (
        <div className="space-y-3">
          {/* Already Inwarded Devices Info (Previous GRNs) */}
          {inwardedDevices.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-lg p-3 text-sm">
              <DeviceIdsViewer
                deviceIds={inwardedDevices}
                title={`Already Inwarded`}
              />
              <p className="text-[10px] text-slate-500 mt-1 italic">
                * These devices were successfully received in previous partial
                GRNs
              </p>
            </div>
          )}

          {/* Scan Button */}
          <div className="space-y-1.5">
            <button
              id={`warehouse-inward-receive-item-${item.inventoryItemCode}-scan-btn`}
              type="button"
              disabled={
                (currentInwardOrder?.type === "PO" &&
                  pending - matchedDevices.length <= 0) ||
                (currentInwardOrder?.type === "ST" &&
                  remainingForST - matchedDevices.length <= 0) ||
                isProcessing ||
                isBlockedByDispute
              }
              onClick={() => setScanning((v) => !v)}
              className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-lg font-semibold text-sm transition-all
                ${
                  isBlockedByDispute
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed opacity-75 border border-dashed border-gray-300"
                    : "bg-purple-200 text-purple-700 hover:bg-purple-300 disabled:opacity-50 disabled:cursor-not-allowed"
                }
              `}
              title={
                isBlockedByDispute
                  ? "Scanning blocked due to unresolved disputes"
                  : ""
              }
            >
              <Icon path={mdiBarcodeScan} size={0.7} />
              {isProcessing
                ? "Processing..."
                : scanning
                  ? "Close Scanner"
                  : "Open Scan Interface"}
            </button>
            {isBlockedByDispute && (
              <div className="flex items-center gap-1.5 px-1 py-0.5 text-red-500 animate-in fade-in slide-in-from-top-1">
                <Icon path={mdiAlertCircle} size={0.5} />
                <span className="text-xs text-color-destructive">
                  Scanning Blocked: Resolve disputes to continue
                </span>
              </div>
            )}
          </div>

          {scanning && (
            <div className="mt-4">
              <DeviceScanModalInward
                open={scanning}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                showBulkTabs={bulkInwardAllowed}
                onClose={() => setScanning(false)}
              >
                {/* SCAN TAB */}
                {activeTab === "scan" && (
                  <div className="space-y-4">
                    {/* Hardware Barcode Scanner Input */}
                    <div className="space-y-2 bg-blue-50/50 p-4 rounded-lg border border-blue-100">
                      <div className="flex items-center gap-2 mb-1">
                        <Icon
                          path={mdiBarcodeScan}
                          size={0.6}
                          className="text-blue-600"
                        />
                        <label className="text-xs font-bold text-gray-700 uppercase tracking-tight">
                          Hardware Barcode Scanner
                        </label>
                      </div>
                      <div className="flex w-full">
                        <input
                          id={`warehouse-inward-receive-item-${item.inventoryItemCode}-barcode-input`}
                          ref={barcodeInputRef}
                          type="text"
                          autoFocus
                          value={barcodeInputValue}
                          onChange={(e) => setBarcodeInputValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && barcodeInputValue.trim()) {
                              handleScanResult(barcodeInputValue.trim());
                              setBarcodeInputValue("");
                            }
                          }}
                          placeholder="Scan or type device ID..."
                          className="w-full rounded-md border-2 border-primary/30 p-3 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all shadow-inner bg-white"
                        />
                      </div>
                    </div>

                    <div className="relative flex items-center py-2">
                      <div className="flex-grow border-t border-gray-200"></div>
                      <span className="flex-shrink mx-4 text-gray-400 text-[10px] font-bold uppercase tracking-wider">
                        OR SCAN VIA CAMERA
                      </span>
                      <div className="flex-grow border-t border-gray-200"></div>
                    </div>

                    <div className="rounded-md border-2 border-gray-100 overflow-hidden h-[260px] md:h-68 max-w-sm mx-auto relative group bg-black shadow-lg">
                      <QrScanner
                        constraints={{ facingMode: "environment" }}
                        onDecode={(result) => {
                          if (result && !isProcessing) handleScanResult(result);
                        }}
                        onError={(err) => {
                          console.error(err);
                          showToast({
                            type: ToastType.error,
                            text: "Camera error. Use barcode scanner or manual entry.",
                          });
                        }}
                        containerStyle={{ width: "100%", height: "100%" }}
                        videoStyle={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />

                      {isProcessing && (
                        <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                          <div className="bg-white rounded-lg px-4 py-2 text-sm font-bold flex items-center gap-2 shadow-xl border border-blue-200">
                            <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                            Processing...
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {bulkInwardAllowed && activeTab === "bulk" && (
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-gray-600">
                      Paste 6-character device IDs (one per line or
                      comma-separated)
                    </label>
                    <textarea
                      id={`warehouse-inward-receive-item-${item.inventoryItemCode}-bulk-input`}
                      rows={6}
                      value={bulkInput}
                      onChange={(e) => setBulkInput(e.target.value)}
                      className="w-full rounded-md border p-3 text-sm font-mono"
                      placeholder={"123456\n234567\n345678"}
                    />
                    <button
                      id={`warehouse-inward-receive-item-${item.inventoryItemCode}-bulk-add-btn`}
                      type="button"
                      onClick={handleBulkPaste}
                      disabled={isProcessing || !bulkInput.trim()}
                      className="w-full bg-blue-600 text-white rounded-md py-2 text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                    >
                      Validate & Add Device IDs
                    </button>
                  </div>
                )}

                {bulkInwardAllowed && activeTab === "upload" && (
                  <div className="space-y-3">
                    <label className="text-xs font-semibold text-gray-600 block">
                      Upload CSV file with device IDs
                    </label>
                    <input
                      id={`warehouse-inward-receive-item-${item.inventoryItemCode}-csv-input`}
                      type="file"
                      accept=".csv"
                      className="block w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleCSVUpload(file);
                        e.target.value = "";
                      }}
                    />
                    <div className="bg-gray-50 rounded p-2 text-xs text-gray-600 space-y-1">
                      <p className="font-semibold">CSV Format:</p>
                      <p>• Single column with header "Unique ID"</p>
                      <p>• One 6-character device ID per row</p>
                      <button
                        id={`warehouse-inward-receive-item-${item.inventoryItemCode}-csv-sample-btn`}
                        type="button"
                        onClick={handleDownloadSampleCSV}
                        className="text-blue-600 font-semibold hover:underline"
                      >
                        Download sample CSV
                      </button>
                    </div>
                  </div>
                )}

                {/* Currently Scanned Devices (Current Session) */}
                {matchedDevices.length > 0 && (
                  <div className="bg-white border border-purple-200 rounded-lg p-1 space-y-2">
                    <div className="flex justify-between items-center px-3 pt-2">
                      <p className="text-xs font-bold text-purple-700 uppercase">
                        Current Session Scans
                      </p>
                      <button
                        id={`warehouse-inward-receive-item-${item.inventoryItemCode}-clear-scanned-modal`}
                        onClick={handleClearAll}
                        className="text-[10px] font-bold text-red-500 hover:text-red-700 uppercase tracking-tighter"
                      >
                        Clear All
                      </button>
                    </div>
                    <DeviceIdsViewer
                      deviceIds={matchedDevices}
                      title={`Currently Scanned Devices`}
                    />
                  </div>
                )}

                {/* Rejected Devices (in-modal) */}
                {renderRejectedDevices()}

                {/* Footer */}
                <div className="flex gap-2 pt-4 border-t mt-4">
                  {matchedDevices.length > 0 && (
                    <button
                      id={`warehouse-inward-receive-item-${item.inventoryItemCode}-clear-scanned-footer`}
                      type="button"
                      onClick={handleClearAll}
                      className="flex-1 border border-red-300 text-red-600 rounded-md py-2 text-sm font-semibold hover:bg-red-50 transition"
                    >
                      Clear All
                    </button>
                  )}

                  <button
                    id={`warehouse-inward-receive-item-${item.inventoryItemCode}-done-scanning`}
                    type="button"
                    onClick={() => setScanning(false)}
                    className="flex-1 bg-gray-100 text-gray-700 rounded-md py-2 text-sm font-semibold hover:bg-gray-200 transition"
                  >
                    Done Scanning
                  </button>
                </div>
              </DeviceScanModalInward>
            </div>
          )}

          {/* Missing Devices Warning (ST Only) */}
          {currentInwardOrder?.type === "ST" && missingDevices.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 space-y-2">
              <div className="flex items-center gap-2 text-sm font-semibold text-yellow-800">
                <Icon path={mdiAlertCircle} size={0.7} />
                Allocated Devices ({missingDevices.length})
              </div>
              <p className="text-xs text-yellow-700">
                These devices were allocated but haven't been scanned yet
              </p>
              <div className="max-h-32 overflow-y-auto">
                <div className="flex flex-wrap gap-1">
                  {missingDevices.slice(0, 20).map((id, i) => (
                    <span
                      key={i}
                      className="px-2 py-0.5 border border-yellow-300 rounded bg-white font-mono text-xs text-yellow-800"
                    >
                      {id}
                    </span>
                  ))}
                  {missingDevices.length > 20 && (
                    <span className="text-xs text-yellow-700 px-2 py-0.5">
                      +{missingDevices.length - 20} more
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Currently Scanned Devices (Current Session) */}
          {matchedDevices.length > 0 && (
            <div className="bg-white border border-purple-200 rounded-lg p-1 space-y-2">
              <div className="flex justify-between items-center px-3 pt-2">
                <p className="text-xs font-bold text-purple-700 uppercase">
                  Current Session Scans
                </p>
                <button
                  id={`warehouse-inward-receive-item-${item.inventoryItemCode}-clear-scanned-panel`}
                  onClick={handleClearAll}
                  className="text-[10px] font-bold text-red-500 hover:text-red-700 uppercase tracking-tighter"
                >
                  Clear All
                </button>
              </div>
              <DeviceIdsViewer
                deviceIds={matchedDevices}
                title={`Currently Scanned Devices`}
              />
            </div>
          )}

          {/* Rejected Devices */}
          {renderRejectedDevices()}
        </div>
      )}
    </div>
  );
};

export default React.memo(InwardReceiveItemCardST);
